import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Edit as ViewIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as RenewIcon,
  VpnKey as LicenseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { licensesApi, Subscription, Product, License } from '@/api/licenses-api';
import { usersApi, Customer } from '@/api/users-api';
import { chargebeeApi } from '@/api/chargebee-api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subscription-tabpanel-${index}`}
      aria-labelledby={`subscription-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openAddLicenseDialog, setOpenAddLicenseDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [licenseForm, setLicenseForm] = useState({
    licenseKey: '',
    expirationDate: '',
    status: 'issued' as 'issued' | 'active' | 'expired',
  });

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const subscriptionData = await licensesApi.getSubscription(id);
        setSubscription(subscriptionData);

        const customerData = await usersApi.getCustomer(subscriptionData.customerId);
        setCustomer(customerData);

        const productData = await licensesApi.getProduct(subscriptionData.productId);
        setProduct(productData);

        const licensesData = await licensesApi.getLicenses(id);
        setLicenses(licensesData);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError('Failed to load subscription data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && user && id) {
      fetchSubscriptionData();
    }
  }, [authLoading, user, id]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    try {
      await chargebeeApi.cancelSubscription(subscription.chargebeeSubscriptionId);
      await licensesApi.updateSubscription(subscription.subscriptionId, {
        status: 'cancelled',
      });

      setOpenCancelDialog(false);
      setSuccessMessage('Subscription cancelled successfully');
      // Refresh data
      const updated = await licensesApi.getSubscription(subscription.subscriptionId);
      setSubscription(updated);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
    }
  };

  const handleAddLicense = async () => {
    if (!subscription) return;
    try {
      const today = new Date();
      const issueDate = today.toISOString().split('T')[0];

      await licensesApi.createLicense({
        subscriptionId: subscription.subscriptionId,
        licenseKey: licenseForm.licenseKey,
        status: licenseForm.status,
        issueDate,
        expirationDate: licenseForm.expirationDate,
      });

      setOpenAddLicenseDialog(false);
      setSuccessMessage('License created successfully');

      const updatedLicenses = await licensesApi.getLicenses(subscription.subscriptionId);
      setLicenses(updatedLicenses);

      setLicenseForm({
        licenseKey: '',
        expirationDate: '',
        status: 'issued',
      });
    } catch (err) {
      console.error('Error creating license:', err);
      setError('Failed to create license. Please try again.');
    }
  };

  const licensesColumns: Column<License>[] = [
    {
      id: 'licenseKey',
      label: 'License Key',
      minWidth: 200,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value) => <StatusChip status={value} type="license" />,
    },
    {
      id: 'issueDate',
      label: 'Issue Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'expirationDate',
      label: 'Expiration Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">You must be logged in to view this page.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subscriptions')}
          sx={{ mt: 2 }}
        >
          Back to Subscriptions
        </Button>
      </Box>
    );
  }

  if (!subscription || !customer || !product) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Subscription not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subscriptions')}
          sx={{ mt: 2 }}
        >
          Back to Subscriptions
        </Button>
      </Box>
    );
  }

  const today = new Date();
  const endDate = new Date(subscription.endDate);
  const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 30;
  const isExpired = daysRemaining <= 0;

  return (
    <>
      <PageHeader
        title={`Subscription: ${subscription.chargebeeSubscriptionId.substring(0, 8)}...`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Subscriptions', href: '/subscriptions' },
          { label: `Subscription ${subscription.chargebeeSubscriptionId.substring(0, 8)}...` },
        ]}
        action={
          subscription.status === 'active' ? {
            label: 'Cancel Subscription',
            onClick: () => setOpenCancelDialog(true),
            icon: <CancelIcon />,
          } : undefined
        }
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md:8}}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12}}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6">Subscription Details</Typography>
                      <StatusChip status={subscription.status} type="subscription" size="medium" />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Subscription ID
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">{subscription.chargebeeSubscriptionId}</Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {customer.name}
                      <Button 
                        size="small" 
                        sx={{ ml: 1 }}
                        onClick={() => navigate(`/customers/${customer.customerId}`)}
                      >
                        View
                      </Button>
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Product
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">{product.description}</Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {new Date(subscription.startDate).toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      End Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {new Date(subscription.endDate).toLocaleDateString()}
                      {isExpiringSoon && (
                        <Chip 
                          label={`Expires in ${daysRemaining} days`} 
                          color="warning" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                      {isExpired && (
                        <Chip 
                          label="Expired" 
                          color="error" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Renewal Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {subscription.renewalDate ? new Date(subscription.renewalDate).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4}}>
              <Card sx={{ bgcolor: 'primary.light', color: 'white', mb: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <LicenseIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h4">{licenses.length}</Typography>
                  <Typography variant="body1">Licenses Issued</Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2, bgcolor: 'white', color: 'primary.dark', '&:hover': { bgcolor: 'primary.contrastText' } }}
                    onClick={() => setOpenAddLicenseDialog(true)}
                    startIcon={<AddIcon />}
                    disabled={subscription.status !== 'active'}
                  >
                    Issue New License
                  </Button>
                </CardContent>
              </Card>
              
              {subscription.status === 'active' && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<RenewIcon />}
                  sx={{ mb: 1 }}
                  onClick={() => navigate(`/subscriptions/renew/${subscription.subscriptionId}`)}
                >
                  Renew Subscription
                </Button>
              )}
              
              {subscription.status === 'cancelled' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This subscription has been cancelled and cannot be modified.
                </Alert>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="subscription tabs">
          <Tab label="Licenses" id="subscription-tab-0" />
          <Tab label="Billing History" id="subscription-tab-1" />
          <Tab label="Activity Log" id="subscription-tab-2" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <DataTable
          columns={licensesColumns}
          data={licenses}
          getRowId={(row) => row.licenseId}
          onRowClick={(row) => navigate(`/licenses/${row.licenseId}`)}
          emptyMessage="No licenses found for this subscription."
          actions={[
            {
              icon: <ViewIcon />,
              label: 'View License',
              onClick: (row) => navigate(`/licenses/${row.licenseId}`),
            },
          ]}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Alert severity="info">
          Billing history will be implemented in a future update.
        </Alert>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Alert severity="info">
          Activity log will be implemented in a future update.
        </Alert>
      </TabPanel>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to cancel this subscription?
          </Typography>
          <Typography variant="body2" color="error" paragraph>
            This action cannot be undone. All associated licenses will remain active until their expiration date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No, Keep Subscription</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
          >
            Yes, Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add License Dialog */}
      <Dialog
        open={openAddLicenseDialog}
        onClose={() => setOpenAddLicenseDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Issue New License</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="License Key"
              value={licenseForm.licenseKey}
              onChange={(e) => setLicenseForm({ ...licenseForm, licenseKey: e.target.value })}
              required
              helperText="Enter a unique license key or identifier"
            />
            
            <TextField
              select
              fullWidth
              margin="normal"
              label="Status"
              value={licenseForm.status}
              onChange={(e) => setLicenseForm({ 
                ...licenseForm, 
                status: e.target.value as 'issued' | 'active' | 'expired' 
              })}
              required
            >
              <MenuItem value="issued">Issued</MenuItem>
              <MenuItem value="active">Active</MenuItem>
            </TextField>
            
            <TextField
              fullWidth
              margin="normal"
              label="Expiration Date"
              type="date"
              value={licenseForm.expirationDate}
              onChange={(e) => setLicenseForm({ ...licenseForm, expirationDate: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
              helperText="When will this license expire?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddLicenseDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLicense}
            disabled={!licenseForm.licenseKey || !licenseForm.status || !licenseForm.expirationDate}
          >
            Create License
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success notification */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
